import { FieldLabel } from 'src/components/Field';
import { FormProvider, useForm } from 'react-hook-form';
import {
  Button,
  ButtonText,
  FormFeedback,
  Input,
  useToast
} from '@morressier/alembic';
import { Flex, Spinner, Text } from '@chakra-ui/react';
import { useAuthRealm } from 'src/routes/AuthRealmAccessControl/AuthRealmContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { BrandLogo } from '../SignUpInviteSent';

import { useForgetPasswordMutation } from 'src/api/signUpMutation';
import { validateEmail } from '../Sign-Up/SignUpForm';
import CopyrightFooterSection from 'src/components/User/CopyrightFooterSection';
import { replaceRedirectUrl } from './ResetPassword';

export type ISignUpUser = {
  email: string;
};

export const ForgotPassword = () => {
  const toast = useToast();
  const forgetPasswordMutation = useForgetPasswordMutation();
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email');
  const { loginUrl } = useAuthRealm();
  const navigate = useNavigate();

  const methods = useForm<ISignUpUser>({
    defaultValues: {
      email: email ?? ''
    }
  });

  const {
    handleSubmit,
    trigger,
    getValues,
    register,
    formState: { errors }
  } = methods;

  const handleForgotPassword = async () => {
    const values = getValues();

    const res = await forgetPasswordMutation.mutateAsync(values);

    if (!res.isSuccess) {
      toast({
        variant: 'error',
        title: 'Error',
        description: res?.message?.message
          ? res?.message?.message
          : res?.message?.error
          ? res?.message?.error
          : 'Internal Server Error',
        isClosable: true
      });
    } else {
      toast({
        variant: 'success',
        title: 'Success',
        description: res?.message ? res?.message : 'Internal Server Error',
        isClosable: true
      });
      navigate(`/signin/reset-sent/${values.email}`);
    }
  };

  const handleLogin = () => {
    const newLoginUrl = replaceRedirectUrl(loginUrl, window.location.origin);
    window.location.href = newLoginUrl;
  };

  return (
    <Flex
      height="100%"
      flexGrow={2}
      flexBasis={0}
      minHeight={'100%'}
      direction="column"
    >
      <Flex
        flexDirection="column"
        gap={12}
        width={{ base: '95%', md: '80%' }}
        marginX={'auto'}
        flex={1}
      >
        <Flex justifyContent={'space-between'} alignItems={'end'}>
          <BrandLogo />
        </Flex>
        <Flex flexDirection={'column'} gap={4}>
          <Flex direction={'column'}>
            <Text textStyle="headingTitle">Forgot Password?</Text>
            <Text>
              Enter an email address below, and we will send a link to reset it.
            </Text>
            <FormProvider {...methods}>
              <form
                autoComplete="off"
                onSubmit={handleSubmit(handleForgotPassword)}
              >
                <Flex gap={4} direction="column" marginTop={4}>
                  <FieldLabel label="Email Address" subText="" isRequired />
                  <Input
                    placeholder="Enter your email address"
                    {...register('email', {
                      required: 'Email address is required',
                      pattern: {
                        value: validateEmail,
                        message: 'Please enter a valid email address'
                      }
                    })}
                    onBlur={() => trigger('email')}
                    isInvalid={!!errors.email}
                    autoComplete="off"
                  />
                  {errors.email && (
                    <FormFeedback variant="error">
                      {errors.email?.message}
                    </FormFeedback>
                  )}

                  <Button
                    type="submit"
                    my={3}
                    disabled={forgetPasswordMutation.isLoading}
                  >
                    {forgetPasswordMutation.isLoading ? (
                      <>
                        Loading <Spinner size={'sm'} />
                      </>
                    ) : (
                      ' Get Recovery Link'
                    )}
                  </Button>
                </Flex>
              </form>
              <Flex justifyContent={'center'} gap={1}>
                <Text textStyle={'headingItem'}>
                  Did you remember your password?
                </Text>
                <ButtonText onClick={handleLogin}>Sign In</ButtonText>
              </Flex>
            </FormProvider>
          </Flex>
        </Flex>
      </Flex>
      <CopyrightFooterSection />
    </Flex>
  );
};
